import { css } from '@emotion/react'
import { useMemo } from 'react'

import { typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'

type Props = {
  data: ReadonlyArray<{ middleChapterName: string; littleChapterName: string }>
}

export const SupplementaryLearningCard = ({ data }: Props) => {
  // 중단원 단위로 그룹화 (응답 sequence 순서 보존)
  const sections = useMemo(() => {
    const groups = new Map<string, string[]>()
    for (const { middleChapterName, littleChapterName } of data) {
      const list = groups.get(middleChapterName) ?? []
      list.push(littleChapterName)
      groups.set(middleChapterName, list)
    }
    return Array.from(groups, ([title, descriptions]) => ({ title, descriptions }))
  }, [data])

  return (
    <div css={supplementaryLearningCardStyle}>
      <h3 className="supplementary-learning-card__header">
        <span className="accent">보충 학습</span>이 필요한 중단원&소단원
      </h3>
      {sections.length > 0 ? (
        <div className="supplementary-learning-card__scroll-area">
          {sections.map((section) => (
            <div className="supplementary-section" key={section.title}>
              <strong>{section.title}</strong>
              {section.descriptions.map((description, index) => (
                <p key={`${section.title}-${index}`}>{description}</p>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <p>보충 학습이 필요한 단원이 없습니다.</p>
      )}
    </div>
  )
}

const supplementaryLearningCardStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 384px;
  height: 100%;
  max-height: 525px;
  overflow: hidden;
  border-radius: var(--Radius-300);
  background-color: var(--White);

  .supplementary-learning-card__header {
    ${typo.body02};
    margin: 0;
    padding: 16px;
    border-bottom: 1px solid var(--Colors-Gray-200);
    color: var(--Colors-Gray-900);
    font-weight: 700;

    .accent {
      color: var(--Colors-Red-400);
    }
  }

  .supplementary-learning-card__scroll-area {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    overflow-y: auto;
    ${mediaQuery.underTablet} {
      max-height: 200px;
    }
  }

  .supplementary-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--Colors-Gray-200);

    &:last-child {
      border-bottom: 0;
    }

    > strong {
      ${typo.caption01};
      display: block;
      margin: 0;
      color: var(--Colors-Gray-900);
      font-weight: 700;
      word-break: keep-all;
    }

    > p {
      ${typo.caption01};
      margin: 0;
      color: var(--Colors-Gray-700);
      word-break: keep-all;
    }
  }

  > p {
    ${typo.body02};
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 24px;
    color: var(--Colors-Gray-700);
    text-align: center;
  }

  ${mediaQuery.underTablet} {
    min-width: unset;
    width: 100%;
    height: auto;
  }
`
